import type { ForceAccountTicket } from '@frontend/AppSettings/services/PipelineProjectService';

/**
 * The two force-account money exports (gap review 2.5; the design source's client-call action
 * item at 45:57): an internal FA Daily Report with fault attribution, and a client-facing
 * billing sheet holding ONLY the MSA-billable lines.
 *
 * Pure row-builders in the Long Sheet's exact mold (buildLongSheetRows): arrays of cells the
 * caller hands to XLSX, exported for desk-testing. Every total is summed from the SAME rows the
 * sheet shows — the bid-vs-actual doctrine, because a sheet the client is billed from must
 * reconcile against itself before anyone reconciles it against the MSA.
 *
 * These read the fetched ticket list, whose money the server STRIPS for blind callers — but the
 * export controls are additionally gated on financial visibility (the Long Sheet rule: no
 * server-side stripping can follow an export, so for a blind role the control does not exist).
 *
 * The demo report also carried a knock-on standby cascade section; that needs the downstream
 * standby model, which is 2.7's schema — deliberately absent here rather than faked.
 */

/** The same seven codes the FA table prices by — ONE vocabulary (P4b decision #5). This is
 * the single frontend copy: CommercialTab and FieldTimesheetTab render their pickers from it,
 * so a backend code rename cannot leave the table and the two exported money sheets
 * disagreeing about what a reason is called (review — there were three copies). */
export const FA_REASONS: { code: string; label: string }[] = [
  { code: 'weather', label: 'Weather standby' },
  { code: 'ground', label: 'Ground conditions' },
  { code: 'scope', label: 'Client-directed scope' },
  { code: 'vendor', label: 'Vendor failure' },
  { code: 'breakdown', label: 'Equipment breakdown' },
  { code: 'rework', label: 'Rework' },
  { code: 'production', label: 'Production shortfall' },
];

export const FA_REASON_LABELS: Record<string, string> = Object.fromEntries(
  FA_REASONS.map((r) => [r.code, r.label]),
);

const label = (code: string): string => FA_REASON_LABELS[code] ?? code;

const num = (v: string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

type Cell = string | number;

/**
 * Internal daily report: every FA line with its fault attribution and whether the client or the
 * contractor eats it. One row per ticket line, oldest day first (a report reads forward).
 */
export function buildFaDailyReportRows(
  matterid: string,
  tickets: ForceAccountTicket[],
): Cell[][] {
  const rows: Cell[][] = [
    ['Force-Account Daily Report'],
    [`Project: ${matterid}`],
    [],
    ['Date', 'Ticket', 'Reason', 'Attribution', 'Line', 'Qty', 'Hours', 'MSA rate', 'Line total'],
  ];
  let total = 0;
  let billable = 0;
  const ordered = [...tickets].sort((a, b) => a.fa_date.localeCompare(b.fa_date));
  for (const ticket of ordered) {
    for (const line of ticket.lines) {
      const amount = num(line.line_total);
      total += amount;
      if (ticket.billable) billable += amount;
      rows.push([
        ticket.fa_date,
        ticket.description || '—',
        label(ticket.reason_code),
        ticket.billable ? 'Billable (MSA)' : 'Absorbed',
        line.description || line.kind,
        num(line.qty),
        num(line.hours),
        num(line.fa_rate),
        amount,
      ]);
    }
  }
  rows.push([]);
  rows.push(['', '', '', '', '', '', '', 'FA total', total]);
  rows.push(['', '', '', '', '', '', '', 'Billable to client (MSA)', billable]);
  rows.push(['', '', '', '', '', '', '', 'Absorbed', total - billable]);
  return rows;
}

/**
 * Client-facing billing sheet: ONLY lines from billable tickets. An absorbed line on a client
 * bill is an over-charge; this builder cannot produce one by construction.
 */
export function buildFaBillingRows(
  matterid: string,
  tickets: ForceAccountTicket[],
): Cell[][] {
  const rows: Cell[][] = [
    ['Force-Account Billing (MSA)'],
    [`Project: ${matterid}`],
    [],
    ['Date', 'Description', 'Reason (MSA)', 'Qty', 'Hours', 'MSA rate', 'Amount'],
  ];
  let total = 0;
  const ordered = [...tickets]
    .filter((t) => t.billable)
    .sort((a, b) => a.fa_date.localeCompare(b.fa_date));
  for (const ticket of ordered) {
    for (const line of ticket.lines) {
      const amount = num(line.line_total);
      total += amount;
      rows.push([
        ticket.fa_date,
        line.description || ticket.description || '—',
        label(ticket.reason_code),
        num(line.qty),
        num(line.hours),
        num(line.fa_rate),
        amount,
      ]);
    }
  }
  rows.push([]);
  rows.push(['', '', '', '', '', 'Total billable', total]);
  return rows;
}
